
import { Component, Prop, Watch } from 'vue-property-decorator';
import VWidget from '@/components/VWidget.vue';
import ListBase from '@/lib/listbase';
import DailySalesTable from './daily-sales-table.vue';
import ActionButtons from '../../components/action-buttons.vue';
import ImportChanges from './import-changes.vue';
import {
  processCurrentMonthData,
  processCurrentPastMonthData,
  processPreviousMonthData
} from './utils/daily-sales-processor';
import SummaryDailySales from './summary-daily-sales.vue';
import SummaryTable from './summary-table.vue';
import { calculateTotal } from './utils/daily-sales-table-utils';

interface DailyItem {
  name: string;
  days: { [key: number]: number };
  previousMonthTotal: number;
  currentPastMonthTotal?: number;
}

interface DealerData {
  name: string;
  models: string[];
  items: DailyItem[];
}

@Component({
  components: {
    VWidget,
    DailySalesTable,
    ActionButtons,
    ImportChanges,
    SummaryDailySales,
    SummaryTable
  }
})
export default class SelectedDaily extends ListBase {
  @Prop({ required: true }) readonly rawSalesData!: any;
  @Prop({ required: true }) readonly dealersData!: DealerData[];
  @Prop({ required: true }) readonly dayRange!: { start: number; end: number };
  @Prop({ required: true }) readonly currentMonth!: string;
  @Prop({ required: true }) readonly previousMonth!: string;
  @Prop({ required: true }) readonly viewType!: string;
  @Prop({ required: true }) readonly year!: number;
  @Prop({ required: true }) readonly month!: number;
  @Prop({ required: true }) readonly manufacturer!: string;
  @Prop({ default: false }) readonly loadingData!: boolean;
  @Prop({ required: true }) readonly hasAdmin!: boolean;

  isEdited: boolean = false;
  showImport: boolean = false;
  confirmSave: boolean = false;
  localConfirmSave = false;
  localDealers: DealerData[] = [];

  showSummary: boolean = false;

  listDealers: string[] = [];

  changesTracker: Set<string> = new Set();

  dailySalesSummary: any = {};

  backupLocalDealers: DealerData[] = [];

  @Watch('localConfirmSave')
  onLocalConfirmSaveChanged(val: boolean) {
    if (!val) {
      this.$emit('cancel');
    }
  }

  @Watch('rawSalesData', { immediate: true, deep: true })
  async onRawSalesDataChange(newVal: any) {
    if (!newVal) return;

    this.localDealers = JSON.parse(JSON.stringify(this.dealersData));

    const mainData = newVal.data?.data ?? newVal.data ?? newVal;
    this.dailySalesSummary = mainData;
    this.listDealers = Array.isArray(mainData.dealers) ? mainData.dealers : [];

    const currentMonthArray = mainData.current_month ?? [];
    const previousMonthArray = mainData.previous_month ?? [];
    const currentPastMonthArray = mainData.current_past_month ?? [];

    currentMonthArray.forEach((dealerObj: any) => {
      processCurrentMonthData(
        this.localDealers,
        dealerObj.dealer,
        dealerObj.items,
        this.dayRange,
        this.initializeDays
      );
    });
    previousMonthArray.forEach((dealerObj: any) => {
      processPreviousMonthData(
        this.localDealers,
        dealerObj.dealer,
        dealerObj.items,
        this.dayRange,
        this.initializeDays
      );
    });
    currentPastMonthArray.forEach((dealerObj: any) => {
      processCurrentPastMonthData(
        this.localDealers,
        dealerObj.dealer,
        dealerObj.items,
        this.dayRange,
        this.initializeDays
      );
    });

    this.ensureDealerItemsFromModels();

    this.localDealers = [...this.localDealers];
    this.backupLocalDealers = JSON.parse(JSON.stringify(this.localDealers));
  }

  @Watch('showImport')
  private onShowImportChanged(val: boolean) {
    if (val) {
      this.backupLocalDealers = JSON.parse(JSON.stringify(this.localDealers));
    }
  }

  showConfirmation() {
    this.localConfirmSave = true;
  }

  onEdit() {
    this.isEdited = true;
    this.$emit('edit');
  }

  get summaryDealers() {
    return this.localDealers.map(dealer => {
      return {
        name: dealer.name,
        items: dealer.items.map(item => {
          return {
            name: item.name,
            totalCurrentMonth: calculateTotal(item.days),
            totalPreviousMonth: item.previousMonthTotal,
            totalCurrentMonthLastYear: item.currentPastMonthTotal || 0
          };
        })
      };
    });
  }

  get isDealer(): boolean {
    return false;
  }

  get isMarcaSelected(): boolean {
    return this.localDealers && this.localDealers.length > 0;
  }

  get computedTitle(): string {
    if (this.viewType === 'internal') return 'Reporte de Ventas Diarias - Internos';
    else if (this.viewType === 'external') return 'Reporte de Ventas Diarias - Externos';
    else if (this.viewType === 'total') return 'Reporte de Ventas Diarias - Total';
    else return 'Reporte de Ventas Diarias';
  }

  exportData() {
    const dayHeaders: number[] = [];
    for (let d = this.dayRange.start; d <= this.dayRange.end; d++) {
      dayHeaders.push(d);
    }

    const tableRows = this.localDealers.map(dealer => ({
      label: dealer.name,
      key: dealer.name
    }));

    const dealersData = this.localDealers.map(dealer => {
      if (!dealer.items || dealer.items.length === 0) {
        dealer.items = this.initializeDealerItems(dealer);
      }
      return {
        name: dealer.name,
        models: dealer.models,
        items: dealer.items
      };
    });

    const payload = {
      rangeDays: {
        from: this.dayRange.start,
        to: this.dayRange.end,
        year: this.year,
        month: this.month
      },
      marcaSeleccionada: this.manufacturer,
      dayHeaders,
      tableRows,
      dealersData,
      previousMonth: this.previousMonth,
      currentMonth: this.currentMonth,
      year: this.year,
    };


    this.$store.dispatch('dailyModule/exportInternalDailyReport', payload)
      .then(() => {
        this.$toast.success("Exportación generada con éxito");
      })
      .catch(err => {
        this.$toast.error("Error al exportar los datos");
        console.error("Error in exportData:", err);
      });
  }

  handleDataUpdate(payload: { dealer: string; item: string; day: number; value: number }) {
    this.isEdited = true;
    console.log('[SelectedDaily] 🛠 handleDataUpdate →', payload);
    console.log('[SelectedDaily] Antes de añadir, changesTracker:', Array.from(this.changesTracker));


    const changeKey = `${payload.dealer}||${payload.item}||${payload.day}`;

    this.changesTracker.add(changeKey);

    console.log('[SelectedDaily] Después de añadir, changesTracker:', Array.from(this.changesTracker));

    const dealer = this.localDealers.find(d => d.name === payload.dealer);
    if (dealer) {
      if (!dealer.items || dealer.items.length === 0) {
        dealer.items = this.initializeDealerItems(dealer);
      }
      const itemObj = dealer.items.find((i: any) => i.name === payload.item);
      if (itemObj) {
        itemObj.days[payload.day] = payload.value;
      } else {
        console.warn(`Item "${payload.item}" not found for dealer "${payload.dealer}"`);
      }
    } else {
      console.warn(`Dealer "${payload.dealer}" not found in localDealers`);
    }
    this.$emit('update-data', payload);
    this.$emit('edit');
  }

  private initializeDays(): { [key: number]: number } {
    const days: { [key: number]: number } = {};
    for (let d = this.dayRange.start; d <= this.dayRange.end; d++) {
      days[d] = 0;
    }
    return days;
  }

  private initializeDealerItems(dealer: DealerData): DailyItem[] {
    const baseItems = [
      { name: 'WALK IN', days: this.initializeDays(), previousMonthTotal: 0 },
      { name: 'TEST DRIVE', days: this.initializeDays(), previousMonthTotal: 0 }
    ];
    const modelItems = dealer.models.map(model => ({
      name: model,
      days: this.initializeDays(),
      previousMonthTotal: 0
    }));
    return [...baseItems, ...modelItems];
  }

  buildSavePayload() {
    console.log('[SelectedDaily] 🔑 cambios pendientes:', Array.from(this.changesTracker));
    const dealersPayload = [];

    for (const changeKey of this.changesTracker) {
      const [dealerName, itemName, dayStr] = changeKey.split('||');

      const day = parseInt(dayStr);

      const dealer = this.localDealers.find(d => d.name === dealerName);
      if (!dealer) continue;

      const item = dealer.items.find(i => i.name === itemName);
      if (!item) continue;

      let existingRecord = dealersPayload.find(
        r => r.dealer === dealerName && r.model === itemName
      );

      if (!existingRecord) {
        existingRecord = {
          dealer: dealerName,
          model: itemName,
          year: this.year,
          month: this.month,
          days: {},
          manufacter: this.manufacturer
        };
        dealersPayload.push(existingRecord);
      }

      if (item.days[day] >= 0) {
        existingRecord.days[day] = item.days[day];
      }
    }
    const result = {
      dealers: dealersPayload.filter(item => Object.keys(item.days).length > 0)
    };
    console.log('[SelectedDaily] 🚀 buildSavePayload →', JSON.stringify(result, null, 2));

    return result;

  }

  async saveData() {
    this.localConfirmSave = false;
    // console.log('[SelectedDaily] Los datos importados y puestos en la tabla son:', JSON.stringify(this.localDealers, null, 2));
    const payload = this.buildSavePayload();
    console.log("Payload to save:", payload);
    try {
      await this.$store.dispatch('dailyModule/storeDailySales', payload);
      this.$toast.success("Datos guardados con éxito");
      this.isEdited = false;
      this.changesTracker.clear();
      this.confirmSave = false;
      (this.$refs.dailySalesTable as any).resetEditModes();
      this.$emit('data-saved');
    } catch (error) {
      console.error("Error in saveData:", error);
      this.$toast.error("Error al guardar los datos");
    }
  }

  updateTableWithImportedData(importedSheet: { headers: string[]; items?: any[]; rows?: any[] }): void {
    const rows = importedSheet.items || importedSheet.rows;
    console.log('[SelectedDaily] ❗ Rows recibidas para importar:', rows);
    console.log('[SelectedDaily] ❗ localDealers antes:', JSON.parse(JSON.stringify(this.localDealers)));

    if (!rows) {
      console.error("No se encontraron filas en los datos importados.");
      return;
    }

    this.changesTracker.clear();
    this.isEdited = false;

    let currentDealer: string | null = null;

    rows.forEach(row => {
      if (row["DEALER"] && row["DEALER"].trim() !== "") {
        currentDealer = row["DEALER"].trim();
        return;
      }

      if (!currentDealer) {
        console.warn("No se ha definido un dealer actual. Se omite la fila:", row);
        return;
      }

      const itemsData = row["ITEMS"];
      if (!itemsData) {
        console.warn("La fila no tiene información de ITEMS, se omite:", row);
        return;
      }

      const modelName = itemsData["ITEMS"] ? itemsData["ITEMS"].trim() : null;
      if (!modelName || modelName === "-") {
        console.warn("No se encontró un modelo válido en la fila, se omite:", row);
        return;
      }

      let hasAnyNonZero = false;
      importedSheet.headers.forEach(header => {
        const dayNumber = parseInt(header, 10);
        if (!isNaN(dayNumber) && itemsData[header] !== undefined) {
          const value = Number(itemsData[header]);
          if (value >= 0) {
            hasAnyNonZero = true;
          }
        }
      });

      if (!hasAnyNonZero) {
        console.warn(`El modelo "${modelName}" no tiene datos >= 0. Se omite la fila.`);
        return;
      }

      const dealer = this.localDealers.find(d => d.name.toLowerCase() === currentDealer!.toLowerCase());
      if (!dealer) {
        console.warn(`Dealer "${currentDealer}" no se encontró en localDealers.`);
        return;
      }

      if (!dealer.items) {
        this.$set(dealer, 'items', this.initializeDealerItems(dealer));
      }

      let item = dealer.items.find(i => i.name.toLowerCase() === modelName!.toLowerCase());
      if (!item) {
        console.warn(`Item/modelo "${modelName}" no se encontró para el dealer "${currentDealer}". Se añadirá uno nuevo.`);
        item = {
          name: modelName,
          days: this.initializeDays(),
          previousMonthTotal: 0
        };
        dealer.items.push(item);

        if (!dealer.models.includes(modelName)) {
          dealer.models.push(modelName);
        }
      }

      importedSheet.headers.forEach(header => {
        const day = parseInt(header, 10);
        if (!isNaN(day) && itemsData[header] !== undefined) {
          const newValue = Number(itemsData[header]);
          if (item.days[day] !== newValue) {
            item.days[day] = newValue;
            this.changesTracker.add(`${currentDealer}||${modelName}||${day}`);
          }
        }
      });
    });

    this.isEdited = true;
    this.$emit('edit');

    console.log('[SelectedDaily] ✅ localDealers después:', JSON.parse(JSON.stringify(this.localDealers)));
  }

  onImportSuccess() {
    console.log("Import of Daily Sales data successful.");
  }

  private ensureDealerItemsFromModels(): void {
    this.localDealers.forEach(dealer => {
      const baseItems = [
        {
          name: 'WALK IN',
          days: this.initializeDays(),
          previousMonthTotal: 0,
          currentPastMonthTotal: 0
        },
        {
          name: 'TEST DRIVE',
          days: this.initializeDays(),
          previousMonthTotal: 0,
          currentPastMonthTotal: 0
        }
      ];

      if (!dealer.items) {
        dealer.items = [...baseItems];
      } else {
        baseItems.forEach(baseItem => {
          if (!dealer.items.some(i => i.name === baseItem.name)) {
            dealer.items.push(baseItem);
          }
        });
      }

      dealer.models.forEach(modelName => {
        const exists = dealer.items.some(
          item => item.name.toUpperCase() === modelName.toUpperCase()
        );
        if (!exists) {
          dealer.items.push({
            name: modelName,
            days: this.initializeDays(),
            previousMonthTotal: 0,
            currentPastMonthTotal: 0
          });
        }
      });
    });
  }

  get filteredDealers(): DealerData[] {
    if (this.viewType === 'internal') {
      return this.localDealers.filter(dealer => /RM|REVO/i.test(dealer.name));
    } else if (this.viewType === 'external') {
      return this.localDealers.filter(dealer => !(/RM|REVO/i.test(dealer.name)));
    } else {
      return this.localDealers;
    }
  }

  handleResumen() {
    this.showSummary = true;
  }

  cancelImportedData() {
    this.$toast.info('Importación cancelada. Se han eliminado los datos importados.');

    if (this.backupLocalDealers.length) {
      this.localDealers = JSON.parse(JSON.stringify(this.backupLocalDealers));
    } else {
      this.onRawSalesDataChange(this.rawSalesData);
    }
    this.isEdited = false;
    this.changesTracker.clear();
  }

}
